import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import useDesks from '@/hooks/useDesks';
import { useBundles } from '@/modules/bundles/hooks/useBundles';
import { Bundle } from '@/modules/bundles/models';

import { updateContext } from '../redux/actions';
import { selectContext, selectCurrentBundleName } from '../redux/selectors';

export type BundleOption = { value: string; label: string; deskName: string };

/**
 * Loads every bundle across all desks (matching the legacy `useBundleSection`)
 * and exposes them for the Try It Bundle picker. Selecting one writes
 * `context.global.bundle` on the current context so the next test send imports
 * the bundle's variables.
 */
export const useTryItBundles = () => {
  const dispatch = useDispatch();
  const { desks } = useDesks();
  // `undefined` deskId → the hook's own query stays disabled; we only want
  // the cross-desk fetcher.
  const { getAllBundlesFromDeskIds } = useBundles(undefined);
  const storeContext = useSelector(selectContext);
  const currentBundleName = useSelector(selectCurrentBundleName);

  const [bundles, setBundles] = useState<Bundle[]>([]);

  useEffect(() => {
    if (!desks?.length) {
      return;
    }
    const loadBundles = async () => {
      const allBundles = await getAllBundlesFromDeskIds(desks.map((desk) => desk.desk_id));
      setBundles(allBundles);
    };
    void loadBundles();
  }, [desks, getAllBundlesFromDeskIds]);

  const deskNameById = useMemo(
    () => new Map((desks ?? []).map((desk) => [desk.desk_id, desk.name])),
    [desks]
  );

  const options = useMemo<BundleOption[]>(
    () =>
      bundles.map((bundle) => ({
        value: bundle.bundle_id,
        label: bundle.name,
        // A bundle always belongs to a desk; fall back to '' only for the brief
        // window before `desks` resolves, so grouping never shows `undefined`.
        deskName: deskNameById.get(bundle.desk_id) ?? '',
      })),
    [bundles, deskNameById]
  );

  const selectedBundleId = useMemo(
    () => bundles.find((bundle) => bundle.name === currentBundleName)?.bundle_id ?? '',
    [bundles, currentBundleName]
  );

  const selectBundle = useCallback(
    (bundleId: string) => {
      const bundle = bundles.find((b) => b.bundle_id === bundleId);
      if (!bundle) {
        return;
      }
      const deskName = deskNameById.get(bundle.desk_id) ?? '';
      // Edit the current (last) context entry — that's what the next send carries.
      const current = storeContext[storeContext.length - 1] ?? {};
      // Merge the bundle's variables into `global.bundle` and stamp its name/desk
      // so `selectedBundleId` can resolve the active option back from context.
      dispatch(
        updateContext([
          ...storeContext.slice(0, -1),
          {
            ...current,
            global: {
              ...current.global,
              bundle: { ...bundle.context, bundle: bundle.name, desk: deskName },
            },
          },
        ])
      );
    },
    [bundles, deskNameById, storeContext, dispatch]
  );

  // Drop the key rather than null it — as if no bundle was ever picked.
  const clearBundle = useCallback(() => {
    const current = storeContext[storeContext.length - 1] ?? {};
    const global = { ...current.global };
    delete global.bundle;
    dispatch(updateContext([...storeContext.slice(0, -1), { ...current, global }]));
  }, [storeContext, dispatch]);

  return { options, selectedBundleId, selectBundle, clearBundle, hasBundles: options.length > 0 };
};
